/** @type {HTMLElement | null} */
let activeDialog = null;
/** @type {HTMLElement | null} */
let lastFocusedElement = null;
/** @type {number} */
let activeVideoIndex = 0;
/** @type {Array<HTMLElement>} */
let videoElements = [];
/** @type {Array<any>} */
let youtubePlayers = [];
/** @type {IntersectionObserver | null} */
let videoObserver = null;

/** @param {HTMLElement} section */
const getShortsScroller = (section) => {
  const slideshow = section?.querySelector('slideshow-component.product-social-shorts__slideshow')
    || section?.querySelector('.product-social-shorts__slideshow')
    || null;

  return slideshow?.querySelector('slideshow-slides')
    || section?.querySelector('slideshow-slides')
    || null;
};

/** @param {HTMLElement} section */
const updateShortsArrowStates = (section) => {
  const scroller = getShortsScroller(section);
  const previous = section?.querySelector('.product-social-shorts__slider-arrows .slideshow-control--previous');
  const next = section?.querySelector('.product-social-shorts__slider-arrows .slideshow-control--next');
  if (!scroller || !previous || !next) return;

  const maxLeft = Math.max(scroller.scrollWidth - scroller.clientWidth - 4, 0);
  // @ts-ignore
  previous.disabled = scroller.scrollLeft <= 4;
  // @ts-ignore
  next.disabled = scroller.scrollLeft >= maxLeft;
};

/** @param {HTMLElement} arrow */
const scrollShorts = (arrow) => {
  const section = arrow.closest('section.product-social-shorts');
  if (!section) return;
  const scroller = getShortsScroller(section);

  if (!scroller) return;

  const direction = arrow.classList.contains('slideshow-control--previous') ? -1 : 1;
  const slides = Array.from(scroller.querySelectorAll('slideshow-slide'));
  if (slides.length < 2) return;

  const currentPosition = scroller.scrollLeft || 0;
  const scrollerLeft = scroller.getBoundingClientRect().left;

  const slidePositions = slides.map((slide) => {
    const slideElement = /** @type {HTMLElement} */ (slide);
    return {
      position: Math.max(
        0,
        slideElement.getBoundingClientRect().left - scrollerLeft + currentPosition
      ),
    };
  });

  const target = direction > 0
    ? slidePositions.find((slide) => slide.position > currentPosition + 2) || slidePositions[0]
    : [...slidePositions].reverse().find((slide) => slide.position < currentPosition - 2)
      || slidePositions[slidePositions.length - 1];

  if (!target) return;

  scroller.scrollTo({ left: target.position, behavior: 'smooth' });

  requestAnimationFrame(() => {
    const thisSection = section || arrow.closest('section.product-social-shorts');
    if (thisSection) updateShortsArrowStates(thisSection);
  });
};

/** @param {string} source */
const getYoutubeId = (source) => {
  if (!source) return '';

  try {
    const url = new URL(source, window.location.origin);
    if (url.hostname.includes('youtu.be')) return url.pathname.slice(1).split('/')[0];
    if (url.pathname.includes('/shorts/')) return url.pathname.split('/shorts/')[1]?.split('/')[0] || '';
    if (url.searchParams.get('v')) return url.searchParams.get('v');
  } catch {
    return source.trim();
  }

  return source.trim();
};

/** @param {Function} callback */
const loadYouTubeAPI = (callback) => {
  // @ts-ignore - YouTube API is loaded externally
  if (window.YT && window.YT.Player) {
    callback();
    return;
  }

  // Check if script is already being loaded
  // @ts-ignore - YouTube API is loaded externally
  if (window.onYouTubeIframeAPIReady) {
    // Add callback to queue
    // @ts-ignore - YouTube API is loaded externally
    const existingCallback = window.onYouTubeIframeAPIReady;
    // @ts-ignore - YouTube API is loaded externally
    window.onYouTubeIframeAPIReady = () => {
      existingCallback();
      callback();
    };
    return;
  }

  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  const firstScriptTag = document.getElementsByTagName('script')[0];
  if (firstScriptTag && firstScriptTag.parentNode) {
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  // @ts-ignore - YouTube API is loaded externally
  window.onYouTubeIframeAPIReady = callback;
};

const pauseAllVideos = () => {
  youtubePlayers.forEach((player) => {
    /** @type {any} */
    const p = player;
    if (p && typeof p.pauseVideo === 'function') {
      try {
        p.pauseVideo();
      } catch (e) {
        console.log('Error pausing video:', e);
      }
    }
  });
};

const playActiveVideo = () => {
  const activePlayer = youtubePlayers[activeVideoIndex];
  if (activePlayer && typeof activePlayer.playVideo === 'function') {
    try {
      activePlayer.playVideo();
    } catch (e) {
      console.log('Error playing video:', e);
    }
  }
};

const setActiveVideo = (index) => {
  if (index < 0 || index >= videoElements.length) return;
  if (index === activeVideoIndex && videoElements[index]?.classList.contains('is-active')) return;

  activeVideoIndex = index;

  // Update active class
  videoElements.forEach((element, i) => {
    if (i === index) {
      element.classList.add('is-active');
    } else {
      element.classList.remove('is-active');
    }
  });

  // pauseVideo()/playVideo() are fire-and-forget postMessage calls to the
  // iframe, not blocking calls - there's no need to wait between them.
  // The old 100ms setTimeout here was pure added latency on every switch.
  pauseAllVideos();
  playActiveVideo();

  // Update nav button states
  updateNavButtonStates();
};

/** @param {boolean} smooth Pass false for an instant jump (used on initial open) */
const scrollToActiveVideo = (smooth = true) => {
  const activeElement = videoElements[activeVideoIndex];
  if (activeElement) {
    activeElement.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'center'
    });
  }
};

const goToNextVideo = () => {
  if (activeVideoIndex < videoElements.length - 1) {
    setActiveVideo(activeVideoIndex + 1);
    scrollToActiveVideo();
  }
};

const goToPreviousVideo = () => {
  if (activeVideoIndex > 0) {
    setActiveVideo(activeVideoIndex - 1);
    scrollToActiveVideo();
  }
};

const updateNavButtonStates = () => {
  if (!activeDialog) return;

  /** @type {HTMLButtonElement | null} */
  const prevButton = activeDialog.querySelector('[data-nav="prev"]');
  /** @type {HTMLButtonElement | null} */
  const nextButton = activeDialog.querySelector('[data-nav="next"]');

  if (prevButton) {
    prevButton.disabled = activeVideoIndex === 0;
    prevButton.style.opacity = activeVideoIndex === 0 ? '0.3' : '1';
    prevButton.style.pointerEvents = activeVideoIndex === 0 ? 'none' : 'auto';
  }

  if (nextButton) {
    nextButton.disabled = activeVideoIndex === videoElements.length - 1;
    nextButton.style.opacity = activeVideoIndex === videoElements.length - 1 ? '0.3' : '1';
    nextButton.style.pointerEvents = activeVideoIndex === videoElements.length - 1 ? 'none' : 'auto';
  }
};

/**
 * Watches each video slide and flips the "active" one the instant it becomes
 * mostly visible - during the swipe, not after it settles. This replaces the
 * old debounced scroll/wheel handlers, which recalculated getBoundingClientRect()
 * for every slide only after scrolling stopped, causing both the perceived
 * lag and the "wrong video still playing" bug.
 */
const setupVideoObserver = () => {
  const container = activeDialog?.querySelector('.product-social-shorts__dialog-video-container');
  if (!container) return;

  if (videoObserver) {
    videoObserver.disconnect();
  }

  videoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
        const index = Number(/** @type {HTMLElement} */ (entry.target).dataset.videoIndex);
        if (!Number.isNaN(index) && index !== activeVideoIndex) {
          setActiveVideo(index);
        }
      }
    });
  }, {
    root: container,
    threshold: [0.6],
  });

  videoElements.forEach((el) => videoObserver.observe(el));
};

const closeVideoDialog = () => {
  if (!activeDialog) return;

  if (videoObserver) {
    videoObserver.disconnect();
    videoObserver = null;
  }

  // Pause all YouTube videos
  youtubePlayers.forEach(player => {
    if (player && player.pauseVideo) {
      player.pauseVideo();
    }
  });

  // Clean up players
  youtubePlayers = [];
  videoElements = [];

  activeDialog.remove();
  document.body.classList.remove('product-social-shorts-modal-open');
  document.body.style.overflow = '';
  lastFocusedElement?.focus();
  activeDialog = null;
  lastFocusedElement = null;
  activeVideoIndex = 0;
};

/** @param {HTMLButtonElement} playButton */
const openVideoDialog = (playButton) => {
  const clickedVideoId = getYoutubeId(playButton.dataset.youtubeId || '');
  if (!clickedVideoId) return;

  closeVideoDialog();
  lastFocusedElement = playButton;

  // Get all video data from the section
  const section = playButton.closest('section.product-social-shorts');
  const allPlayButtons = section?.querySelectorAll('[data-youtube-id]') || [];
  const videoData = Array.from(allPlayButtons).map((btn, index) => {
    const button = /** @type {HTMLElement} */ (btn);
    return {
      id: getYoutubeId(button.dataset.youtubeId || ''),
      title: button.getAttribute('aria-label') || `YouTube Short ${index + 1}`,
      index
    };
  }).filter(data => data.id);

  if (videoData.length === 0) return;

  // Find the index of the clicked video
  activeVideoIndex = videoData.findIndex(data => data.id === clickedVideoId);
  if (activeVideoIndex === -1) activeVideoIndex = 0;

  // Create dialog with vertical video slider
  const dialog = document.createElement('div');
  dialog.className = 'product-social-shorts__dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-label', 'YouTube Shorts Viewer');

  const videosHtml = videoData.map((data, index) => `
    <div class="product-social-shorts__video-slide" data-video-index="${index}">
      <div class="product-social-shorts__dialog-video">
        <iframe
          id="youtube-iframe-${index}"
          src="https://www.youtube.com/embed/${encodeURIComponent(data.id)}?enablejsapi=1&rel=0"
          title="${data.title}"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          data-video-index="${index}"
        ></iframe>
      </div>
    </div>
  `).join('');

  dialog.innerHTML = `
    <div class="product-social-shorts__dialog-backdrop" data-close-video></div>
    <div class="product-social-shorts__dialog-panel">
      <button class="product-social-shorts__dialog-close" type="button" aria-label="Close video" data-close-video>&times;</button>
      <button class="product-social-shorts__dialog-nav product-social-shorts__dialog-nav--prev" type="button" aria-label="Previous video" data-nav="prev">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      </button>
      <div class="product-social-shorts__dialog-video-container">
        ${videosHtml}
      </div>
      <button class="product-social-shorts__dialog-nav product-social-shorts__dialog-nav--next" type="button" aria-label="Next video" data-nav="next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
  `;

  document.body.append(dialog);
  document.body.classList.add('product-social-shorts-modal-open');
  document.body.style.overflow = 'hidden';
  activeDialog = dialog;

  // Store video elements
  videoElements = Array.from(dialog.querySelectorAll('.product-social-shorts__video-slide'));

  // Set the active class and jump straight to the clicked video right away -
  // this does not depend on the YouTube API and should not wait for it.
  // Using an instant (non-smooth) scroll here means the popup opens already
  // showing the right video instead of visibly scrolling down to it.
  videoElements.forEach((element, i) => {
    element.classList.toggle('is-active', i === activeVideoIndex);
  });
  updateNavButtonStates();
  requestAnimationFrame(() => scrollToActiveVideo(false));

  // Watch slides so the active one updates - and others pause - the instant
  // a new slide becomes mostly visible, instead of waiting for scroll to stop.
  setupVideoObserver();

  // Load YouTube IFrame API
  loadYouTubeAPI(() => {
    // Initialize players
    videoData.forEach((data, index) => {
      const iframe = document.getElementById(`youtube-iframe-${index}`);
      // @ts-ignore - YouTube API is loaded externally
      if (iframe && window.YT) {
        // @ts-ignore - YouTube API is loaded externally
        const player = new YT.Player(iframe, {
          events: {
            'onReady': (/** @type {any} */ event) => {
              youtubePlayers[index] = event.target;
              // If this is the active video, play it; otherwise pause it
              if (index === activeVideoIndex) {
                event.target.playVideo();
              } else {
                event.target.pauseVideo();
              }
            }
          }
        });
      }
    });
  });

  // Focus close button
  const closeButton = dialog.querySelector('.product-social-shorts__dialog-close');
  if (closeButton) {
    (/** @type {HTMLElement} */ (closeButton)).focus();
  }
};

document.addEventListener('click', (/** @type {MouseEvent} */ event) => {
  if (!(event.target instanceof Element)) return;

  const target = /** @type {Element} */ (event.target);
  const arrow = target.closest('.product-social-shorts__slider-arrows .slideshow-control, .product-social-shorts__slideshow .slideshow-control');
  if (arrow) {
    event.preventDefault();
    event.stopImmediatePropagation();
    scrollShorts(/** @type {HTMLElement} */ (arrow));
    return;
  }

  const playButton = target.closest('[data-youtube-id]');
  if (playButton) {
    openVideoDialog(/** @type {HTMLButtonElement} */ (playButton));
    return;
  }

  // Handle popup navigation buttons
  if (activeDialog) {
    const navButton = target.closest('[data-nav]');
    if (navButton) {
      event.stopPropagation();
      const button = /** @type {HTMLElement} */ (navButton);
      const direction = button.dataset.nav;
      if (direction === 'next') {
        goToNextVideo();
      } else if (direction === 'prev') {
        goToPreviousVideo();
      }
      return;
    }

    // Handle close button and backdrop click
    if (target.closest('[data-close-video]')) {
      closeVideoDialog();
      return;
    }

    // Handle backdrop click (outside video content)
    const backdrop = target.closest('.product-social-shorts__dialog-backdrop');
    if (backdrop) {
      closeVideoDialog();
      return;
    }
  }
}, true);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('section.product-social-shorts').forEach((section) => {
    // @ts-ignore
    const scroller = getShortsScroller(section);
    if (!scroller) return;

    // @ts-ignore
    updateShortsArrowStates(section);
    scroller.addEventListener('scroll', () => {
      // @ts-ignore
      updateShortsArrowStates(section);
    });
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeVideoDialog();
    return;
  }

  // Only handle arrow keys when popup is open
  if (!activeDialog) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    goToNextVideo();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    goToPreviousVideo();
  }
});